<template>
    <v-container>
        <div v-if="!loading">
            <v-row>
                <v-col>
                    <h4 class="text-h4">Landed Cost - {{ data.code }}</h4>
                    <p class="text-subtitle-1">Landed Cost Information</p>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                 <v-col cols="12" md="3" v-if="printable && hasPermission(permissions.landed_cost.print)">
                    <v-btn depressed block color="primary" @click="handlePrint" :loading="printLoading">
                        <v-icon left>mdi-printer</v-icon>
                        print
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="postable && hasPermission(permissions.landed_cost.post)">
                    <v-btn depressed block color="primary" @click="handlePost" :loading="postLoading">
                        <v-icon left>mdi-post</v-icon>
                        post
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="editable && hasPermission(permissions.landed_cost.update)" @click="handleEdit">
                    <v-btn depressed block>
                        <v-icon left>mdi-pencil</v-icon>
                        edit
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="deletable && hasPermission(permissions.landed_cost.delete)" @click="handleRemove" :loading="removeLoading">
                    <v-btn depressed block color="error--text">
                        <v-icon left>mdi-delete</v-icon>
                        delete
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Code</th>
                                <td>{{ data.code }}</td>
                            </tr>
                            <tr>
                                <th>Date</th>
                                <td>{{ data.date }}</td>
                            </tr>
                            <tr>
                                <th>State</th>
                                <td>
                                    <PStatus :state="data.state"/>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-subheader>Landed Cost Components</v-subheader>
            <v-row>
                <v-col cols="12" md="6">
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Cost Component</th>
                                <th>Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(d, i) in data.component_lines" :key="i">
                                <td>{{ i + 1 }}</td>
                                <td>
                                    <span v-if="d.component">
                                        {{ d.component.name }}
                                    </span>
                                </td>
                                <td class="font-weight-bold">{{ formatNumber(d.cost, 2) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colspan="2">Total</th>
                                <th>{{ formatNumber(getTotal(data.component_lines, "cost"), 2) }}</th>
                            </tr>
                        </tfoot>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-subheader>Landed Cost Items</v-subheader>
            <v-row>
                <v-col>
                    <v-simple-table dense>
                        <thead>
                            <tr class="font-weight-bold">
                                <th>#</th>
                                <th>Item</th>
                                <th>Unit</th>
                                <th>Quantity</th>
                                <th>Unit Value</th>
                                <th>Total Value</th>
                                <th>Unit Cost</th>
                                <th>Total Cost</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(d, i) in data.lines" :key="i">
                                <td>{{ i + 1 }}</td>
                                <td>
                                    <span v-if="d.item">
                                        {{ d.item.code }} - {{ d.item.name }}
                                    </span>
                                </td>
                                <td>
                                    <span v-if="d.measure_unit">
                                        {{ d.measure_unit.abbreviation }}
                                    </span>
                                </td>
                                <td>{{ formatNumber(d.quantity, 2) }}</td>
                                <td>{{ formatNumber(d.unit_value, 2) }}</td>
                                <td>{{ formatNumber(d.quantity * d.unit_value, 2) }}</td>
                                <td>{{ formatNumber(d.unit_cost, 2) }}</td>
                                <td>{{ formatNumber(d.quantity * d.unit_cost, 2) }}</td>
                                <td class="font-weight-bold">{{ formatNumber(d.quantity * d.unit_cost, 2) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="font-weight-bold">
                                <td colspan="7">Total</td>
                                <td>{{ formatNumber(totalCost, 2) }}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Remarks</th>
                                <td>{{ data.remarks }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Created By</th>
                                <td>
                                    <span v-if="data.created_by">
                                        {{ data.created_by.name }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Created At</th>
                                <td>{{ data.created_at }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col cols="12" md="4" v-if="data.posted_at">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Updated By</th>
                                <td>
                                    <span v-if="data.updated_by">
                                        {{ data.updated_by.name }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Updated At</th>
                                <td>{{ data.updated_at }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col cols="12" md="4" v-if="data.posted_at">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Posted By</th>
                                <td>
                                    <span v-if="data.posted_by">
                                        {{ data.posted_by.name }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Posted At</th>
                                <td>{{ data.posted_at }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
        </div>
        <div v-else>
            <v-row>
                <v-col>
                    <v-skeleton-loader type="heading" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="d-flex">
                    <v-skeleton-loader type="button" class="my-4 me-4"/>
                    <v-skeleton-loader type="button" class="my-4"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
        </div>
        <PAlert v-bind="alert" v-model="alert.display"/>
    </v-container>
</template>

<script>
import landedCost from './../../services/purchase/landedCost';
import PStatus from './../../components/common/PStatus.vue';
import { formatNumber } from 'accounting'
import {  getTotal } from './../../utils/accounting';
import PAlert from '../../components/common/PAlert.vue';
import { mapActions } from 'vuex';
import permissions from '../../data/permissions';
import authorize from '../../utils/authorize';

export default {
    components: {
        PStatus,
        PAlert
    },
    data() {
        return {
            id: null,
            data: {},
            loading: false,
            alert: {
                display: false,
                title: "Alert",
                content: "Are you sure?",
                callback: null
            },
            removeLoading: false,
            postLoading: false,
            printLoading: false,
            permissions
        }
    },
    computed: {
        editable() {
            return this.data && this.data.meta && this.data.meta.editable;
        },
        deletable() {
            return this.data && this.data.meta && this.data.meta.deletable;
        },
        postable() {
            return this.data && this.data.meta && this.data.meta.postable;
        },
        printable() {
            return this.data && this.data.meta && this.data.meta.printable;
        },
        totalCost() {
            let totalCost = 0;
            if(!this.data) return totalCost;
            let lines = this.data.lines;
            if(!Array.isArray(lines)) return totalCost;
            for(const line of lines) {
                totalCost += line.quantity * line.unit_cost;
            }
            return totalCost;
        }
    },
    methods: {
        ...mapActions({
            snackbar: "ui/snackbar/show"
        }),
        hasPermission(code) {
            return authorize(code);
        },
        formatNumber,
        getTotal,
        fetch() {
            this.loading = true;
            landedCost.find(this.id).then(res => {
                this.data = res.data;
            }).finally(() => {
                this.loading = false;
            })
        },
        handleEdit() {
            this.$router.push({
                name: "LandedCostEdit",
                params: {
                    id: this.id
                }
            })
        },
        handleRemove() {
            this.alert = { ...this.alert, display: true, content: "Are you sure you want to delete?", callback: this.remove };
        },
        remove() {
            this.removeLoading = true;
            landedCost.remove(this.id).then(() => {
                this.$router.replace({
                    name: "LandedCostList"
                })
            }).finally(() => {
                this.removeLoading = false;
            })
        },
        handlePost() {
            this.alert = { ...this.alert, display: true, content: "Are you sure you want to submit for posting?", callback: this.post };
        },
        handlePrint() {
            this.printLoading = true;
            landedCost.print(this.id)
                .then(response => {
                    var printWindow = window.open('','','width=800,height=600');
                    printWindow.document.write(response.data);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                })
                .catch(error => {
                    let message = error.response ? error.response.data.message : error;
                    this.snackbar = { value: true, color: "error", text: message }
                }).finally(() => {
                    this.printLoading = false;
                });
        },
        post() {
            this.postLoading = true;
            landedCost.post(this.id).then(() => {
                this.snackbar({color: "success", text: "Landed Cost Submitted for Posting"})
                setTimeout(() => {
                    this.fetch();
                }, 800)
            }).finally(() => {
                this.postLoading = false;
            })
        }
    },
    created() {
        this.id = this.$route.params.id;
        this.fetch();
    }
}
</script>

<style>

</style>